// eslint-disable-next-line import/no-cycle
// eslint-disable-next-line import/no-cycle
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import classnames from 'classnames';

import { useMediaQuery } from '@mui/material';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityName from 'components/Globals/EntityName';
// eslint-disable-next-line import/no-cycle
import useQuickView, { QUICK_VIEW_TYPES } from 'components/Globals/Layout/QuickView';

import { DATE_FORMATS, ENTITY_TYPE_MAP, TP, URL_SLUG_TYPES } from 'constants/index';

import { findCreditGroupByRole, getBooleanSearchQuery } from 'utils/casting';
import ImpressionTracker from 'utils/components/impressionTracker';
import { createDate } from 'utils/date';
import { getCityCountry } from 'utils/globals';
import useAppContext from 'utils/hooks/useAppContext';
import { useDialogs } from 'utils/hooks/useDialogs';
import usePageContext from 'utils/hooks/usePageContext';
import { formatGroupedVenueDates } from 'utils/jumpin';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Work/queries';

import { useTranslation } from 'src/i18n';

import { CASTING_RESULT_TYPES_VALUES, ENTITY_TYPES, FILTER_TYPES } from 'operabase-router/constants';

import CreditsWorks from '../CreditsWorks';
import GenreTags from '../GenreTags';
import ProductionPeriod from '../ProductionPeriod';
import { DirectorConductor } from '../renderers';

import classes from './ProSlugCollapsed.module.scss';

const SelectedDatesVenues = ({ venues }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext;
  const { filters } = linkProps || {};
  const { [FILTER_TYPES.JUMP_IN_EXPRESSION]: jumpInExp } = filters || {};
  const isMobile = useMediaQuery('(max-width: 800px)');

  const { venueIds, dates } = useMemo(
    () =>
      (jumpInExp || []).reduce(
        (acc, { entity: { id, date } }) => ({
          venueIds: [...acc.venueIds, id],
          dates: [...acc.dates, date],
        }),
        { venueIds: [], dates: [] },
      ),
    [jumpInExp],
  );

  const [selectedDatesVenues, setSelectedDatesVenues] = useState([]);

  const { workIds, workRole } = useMemo(() => {
    const query = getBooleanSearchQuery(filters?.expression);
    const workIdsValues = query
      .filter(item => item.type === 'QUERY')
      .map(item => item.value.work_id)
      .join(', ');

    return {
      workIds: workIdsValues,
      workRole: query?.[0]?.value?.[URL_SLUG_TYPES.WORK_ROLE],
    };
  }, [filters?.expression]);

  const { data: castCrewData } = useQuery(
    queries.getCastCrewDetails({
      filters: {
        work_id: workIds,
        ...(filters?.[URL_SLUG_TYPES.DATE_FROM] && { [URL_SLUG_TYPES.DATE_FROM]: filters?.[URL_SLUG_TYPES.DATE_FROM] }),
      },
      queryConfig: { enabled: !!workIds },
    }),
  );

  const matchedRole = useMemo(() => {
    if (workRole && castCrewData?.length > 0) {
      return findCreditGroupByRole(castCrewData, workRole);
    }

    return null;
  }, [workRole, castCrewData]);

  useEffect(() => {
    if (venueIds?.length > 0 && dates?.length > 0) {
      const venueDates = venueIds.map((venueId, index) => ({
        venue: { id: venueId },
        date: createDate(dates[index]).format(DATE_FORMATS.FULL_DATE),
      }));

      const getSelectedDatesVenues = formatGroupedVenueDates(venueDates, venues);
      setSelectedDatesVenues(getSelectedDatesVenues);
    }
  }, [venueIds, dates, venues]);

  return (
    <div className={classes.selectedRolesAndVenues}>
      {!isMobile && (
        <div className={classes.selectedRolesAndVenues__row}>
          <Typography size="12" italic>
            {t(`${TP}.FN_ROLE_TITLE`)}
          </Typography>
          {castCrewData?.length > 0 && matchedRole?.role?.name && (
            <Typography size="12" weight="bold" className={classes.textDisplay}>
              {matchedRole.role.name}
            </Typography>
          )}
        </div>
      )}
      {selectedDatesVenues?.map((item, index) => (
        <>
          <div className={classes.selectedRolesAndVenues__row} key={index}>
            <Typography size="12" italic className={classes.label}>
              {t(`${TP}.m_VENUE`)}
            </Typography>
            <Typography size="12" className={classes.venueText} weight={isMobile ? 'regular' : 'bold'}>
              {item?.venue}
            </Typography>
          </div>
          <div className={classes.selectedRolesAndVenues__row}>
            <Typography size="12" italic className={classes.label}>
              {t(`${TP}.m_DATES`)}
            </Typography>
            <Typography size="12" className={classes.textDisplay}>
              {item?.dates}
            </Typography>
          </div>
        </>
      ))}
    </div>
  );
};

const ProSlugCollapsed = ({
  isExpanded,
  conciseProduction,
  trackingData,
  onClick,
  entityId,
  entityType,
  showWorkDetails,
  isJumpInSlug,
  handleRoleSelect,
  entity,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { urlSlugs } = usePageContext();
  const { showQuickView } = useQuickView();
  const { obRouteContext } = useAppContext();
  const { isJumpInSelectProductionsModalOpen, setIsJumpInSelectProductionsModalOpen } = useDialogs();
  const { linkProps } = obRouteContext;
  const { tab } = linkProps || {};

  const isMobile = useMediaQuery('(max-width: 1000px)');
  const track = useTracking();
  const isCastingProfile = urlSlugs?.[0]?.entityType === ENTITY_TYPES.PROFILE;
  const isJumpInViewTab = tab === CASTING_RESULT_TYPES_VALUES.JUMP_IN;

  const openQuickPreview = useCallback(
    ({ productionId }) => {
      showQuickView({
        type: QUICK_VIEW_TYPES.PRODUCTION,
        data: {
          entityId: productionId,
          quickViewContextId: entityId,
          handleRoleSelect,
          isJumpInSlug,
        },
      });
    },
    [entityId, showQuickView, handleRoleSelect, isJumpInSlug],
  );

  const {
    conductor,
    director,
    producer,
    venue,
    id,
    performedDates,
    totalDates,
    castCount,
    crewCount,
    instrumentalCount,
    venues,
  } = conciseProduction || {};

  const completeTrackingData = {
    ...trackingData,
    entityId: conciseProduction?.id,
    entityName: conciseProduction?.name,
    entityType: ENTITY_TYPES.PRODUCTION,
  };

  const icon = useMemo(() => {
    if (isMobile) {
      return 'chevron_right';
    }

    if (isExpanded) {
      return 'keyboard_arrow_up';
    }

    return 'expand_more';
  }, [isExpanded, isMobile]);

  const handleOnClick = e => {
    if (isJumpInSlug && !isJumpInSelectProductionsModalOpen) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (isMobile) {
      openQuickPreview({ productionId: id });
    } else {
      onClick(e);

      track.click({
        ...completeTrackingData,
        meta: {
          isOpen: isExpanded,
        },
      });
    }
  };

  return (
    <ImpressionTracker data={completeTrackingData} className={classes.root}>
      <div
        className={classnames(classes.psc, {
          [classes.pscJumpIn]: !!isJumpInSelectProductionsModalOpen,
          [classes.pscJumpInActive]: isJumpInViewTab && isJumpInSlug && !isJumpInSelectProductionsModalOpen,
        })}
        onClick={handleOnClick}
      >
        {!isMobile && (
          <div className={classnames(classes.psc__expandBtn, { [classes.psc__expandBtnHide]: isJumpInSlug })}>
            <LinkButton
              styles={{ root: classes.arrowIcon }}
              variant="text"
              {...(isMobile
                ? { leftIcon: <SpriteIcon icon={icon} size={12} /> }
                : { rightIcon: <SpriteIcon icon={icon} size={12} /> })}
              disableHover
              disableUnderline
              skipTracking
            />
          </div>
        )}
        <div className={classes.psc__block}>
          <div className={classes.psc__block_left}>
            <div className={classes.tags}>
              <GenreTags production={conciseProduction} styles={{ tagsList: classes.tagsList }} />
            </div>
            <div
              className={classnames(classes.creditWorks, {
                [classes.castingSingleCreditWorks]: !isCastingProfile && !showWorkDetails,
              })}
            >
              <CreditsWorks
                production={conciseProduction}
                entityId={entityId}
                trackingData={trackingData}
                entityType={entityType}
                styles={{
                  workName: classes.workName,
                  composer: classes.composer,
                  customTitle: classnames(classes.customTitle, {
                    [classes.prodCustomTitle]: isCastingProfile || showWorkDetails,
                  }),
                  noProgram: classnames(classes.noProgram, {
                    [classes.artistNoProgram]: isCastingProfile || showWorkDetails,
                  }),
                  workCount: classes.workCount,
                  credit: classes.credit,
                }}
                hasMultiWorks={showWorkDetails}
                isJumpInSlug={isJumpInSlug}
              />
              {isMobile && isJumpInSlug && !isJumpInSelectProductionsModalOpen && (
                <SelectedDatesVenues venues={venues} />
              )}
            </div>
          </div>
          <div className={classes.psc__block_right}>
            <div className={classes.periodDates}>
              <div className={classes.period}>
                <ProductionPeriod production={conciseProduction} entity={entity} trackingData={trackingData} />
              </div>
              {entityType === ENTITY_TYPES.PROFILE && (
                <Typography size={12} className={classes.dates} color="tertiary">
                  {`(${performedDates} / ${totalDates} ${t(`${TP}.m_DATES`)})`}
                </Typography>
              )}
            </div>
            <div className={classes.orgCastCrewWrapper}>
              <div className={classes.orgVenues}>
                <div className={classes.org}>
                  {producer?.id && (
                    <div>
                      <Typography size="12" className={classes.producerName}>
                        <EntityName
                          key={producer.id}
                          entityType={ENTITY_TYPE_MAP[producer?.type]}
                          entity={{ id: producer.id, name: producer?.name }}
                          isRaw
                          trackingData={trackingData}
                        />
                        {conciseProduction?.isProducerDebut && (
                          <Typography size={11} color="secondary" className={classes.producerDebut}>
                            (PD)
                          </Typography>
                        )}
                      </Typography>
                      <Typography size="12" color="secondary" className={classes.producerCityCountry}>
                        {getCityCountry(producer)}
                      </Typography>
                    </div>
                  )}
                </div>
                <div className={classes.venues}>
                  {venue?.id && (
                    <div className={classes.venue}>
                      <Typography size="12" color="tertiary">
                        {t(`${TP}.m_VENUE`)}:{' '}
                      </Typography>
                      <Typography size="12" color="tertiary">
                        <EntityName
                          key={venue.id}
                          entityType={ENTITY_TYPE_MAP[producer?.type]}
                          entity={{ id: venue.id, name: venue?.name }}
                          isRaw
                          trackingData={trackingData}
                        />
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.crewCast}>
                <DirectorConductor
                  directors={director}
                  conductors={conductor}
                  crewCount={crewCount}
                  castCount={castCount}
                  t={t}
                  trackingData={trackingData}
                  isJumpInSlug={isJumpInSlug}
                />
                {!isMobile && isJumpInSlug && !isJumpInSelectProductionsModalOpen && (
                  <SelectedDatesVenues venues={venues} trackingData={trackingData} />
                )}
                <div className={classes.cast}>
                  {instrumentalCount > 0 && !isJumpInSlug && (
                    <Typography size="12" color="tertiary" className={classes.instrumentalCount}>
                      {t(`${TP}.FN_INDUSTRY_PRO_INSTRUMENTALISTS_LABEL`)} <strong>({instrumentalCount})</strong>
                    </Typography>
                  )}
                  {isJumpInSelectProductionsModalOpen && (
                    <LinkButton
                      rightIcon={<SpriteIcon icon="chevron_right" />}
                      shape="text"
                      onClick={handleOnClick}
                      styles={{ root: classes.selectProdBtn }}
                      trackingData={{
                        ...trackingData,
                        subComponent: SUB_COMPONENTS.SELECT_CTA,
                      }}
                    >
                      {t(`${TP}.SELECT`)}
                    </LinkButton>
                  )}
                  {isJumpInSlug && !isJumpInSelectProductionsModalOpen && (
                    <LinkButton
                      rightIcon={<SpriteIcon icon="edit" size={14} />}
                      variant="text"
                      styles={{ root: classes.editBtn }}
                      onClick={() => setIsJumpInSelectProductionsModalOpen(true)}
                      trackingData={{
                        ...trackingData,
                        subComponent: SUB_COMPONENTS.EDIT_CTA,
                      }}
                    >
                      {t(`${TP}.m_EDIT`)}
                    </LinkButton>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isMobile && <div className={classes.divider}></div>}
    </ImpressionTracker>
  );
};

export default ProSlugCollapsed;
